import { ModuleSchema, ObjectSchema, PartSchema } from './registry';

const objectKey = (object: ObjectSchema): string => object.id ?? object.name ?? object.label ?? 'object';
const partKey = (part: PartSchema): string => part.id ?? `group_${part.groupId}`;
const partDisplayTitle = (part: PartSchema): string => part.name ?? part.label ?? part.id ?? `Part ${part.groupId}`;

// 10 popular Bambu Lab filament colors (PLA Basic)
export const BAMBU_LAB_COLORS = [
  { name: 'Jade White',       hex: '#FFFFFF', desc: 'Jade White' },
  { name: 'Basic Black',      hex: '#111827', desc: 'Basic Black' },
  { name: 'Bambu Green',      hex: '#00AE42', desc: 'Bambu Green' },
  { name: 'Flame Red',        hex: '#C8102E', desc: 'Flame Red' },
  { name: 'Cobalt Blue',      hex: '#0055B8', desc: 'Cobalt Blue' },
  { name: 'Sunflower Yellow', hex: '#F7D117', desc: 'Sunflower Yellow' },
  { name: 'Mandarin Orange',  hex: '#FF6A13', desc: 'Mandarin Orange' },
  { name: 'Silver Gray',      hex: '#94A3B8', desc: 'Silver Gray' },
  { name: 'Sakura Pink',      hex: '#F472B6', desc: 'Sakura Pink' },
  { name: 'Lavender Purple',  hex: '#8B5CF6', desc: 'Lavender Purple' },
];

export interface SceneTreeCallbacks {
  onVisibilityChange?: (visibilityMap: Map<number, boolean>) => void;
  onSelect?: (type: 'all' | 'object' | 'part', id: string, groupId?: number) => void;
  onColorChange?: (colorKey: string, hex: string) => void;
  onZoomSelected?: () => void;
  onIsolateToggle?: (isIsolated: boolean) => void;
}

export class SceneTreeViewer {
  public el: HTMLElement;
  private schema: ModuleSchema;
  private callbacks: SceneTreeCallbacks = {};

  private partVisibility: Map<number, boolean> = new Map();
  private partColors: Map<string, string> = new Map();
  private selectedGroupIds: Set<number> = new Set();
  private selectedTarget: { type: 'all' | 'object' | 'part' | 'multi'; id: string; groupId?: number } = { type: 'all', id: 'all' };
  private isIsolated: boolean = false;
  private preIsolateVisibility: Map<number, boolean> = new Map();

  private isolateBtn!: HTMLButtonElement;
  private zoomBtn!: HTMLButtonElement;
  private contentContainer!: HTMLElement;

  constructor(schema: ModuleSchema, callbacks: SceneTreeCallbacks = {}) {
    this.schema = schema;
    this.callbacks = callbacks;

    if (schema.objects) {
      for (const obj of schema.objects) {
        for (const part of obj.parts) {
          this.partVisibility.set(part.groupId, part.visible !== false);
          if (part.colorKey && part.defaultColor) this.partColors.set(part.colorKey, part.defaultColor);
        }
      }
    }

    this.el = document.createElement('div');
    this.el.id = 'scene-tree-viewer';
    this.el.style.cssText = [
      'position: relative',
      'background: rgba(255, 255, 255, 0.94)',
      'border: 1px solid #e2e8f0',
      'border-radius: 8px',
      'font-family: var(--mono-font), monospace',
      'font-size: 11px',
      'color: #1e293b',
      'backdrop-filter: blur(8px)',
      '-webkit-backdrop-filter: blur(8px)',
      'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08)',
      // min-width, not width: the panel must be free to STRETCH to the width of its siblings in
      // left-overlay-col (a fixed width would opt it out of that stretch). 270px is the floor.
      'min-width: 270px',
      'box-sizing: border-box',
      'max-height: calc(100vh - 220px)',
      'display: flex',
      'flex-direction: column',
      'overflow: hidden',
      'transition: box-shadow 0.2s',
      'pointer-events: auto',
    ].join(';');

    this.render();
  }

  private render(): void {
    this.el.innerHTML = '';

    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
      font-weight: 700;
      font-size: 10px;
      letter-spacing: 0.05em;
      color: #475569;
      user-select: none;
    `;
    header.innerHTML = `
      <span>🌳 HIERARCHY (OBJECT & PART)</span>
      <span style="font-size:9px;color:#94a3b8;font-weight:400;">Eye: Hide/Show</span>
    `;
    this.el.appendChild(header);

    this.contentContainer = document.createElement('div');
    this.contentContainer.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 6px 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    `;
    this.el.appendChild(this.contentContainer);

    this.renderTreeNodes();

    const toolbar = document.createElement('div');
    toolbar.style.cssText = `
      display: flex;
      gap: 6px;
      padding: 8px 10px;
      background: #f8fafc;
      border-top: 1px solid #e2e8f0;
    `;

    this.zoomBtn = document.createElement('button');
    this.zoomBtn.textContent = '⛶ Zoom Selected';
    this.zoomBtn.style.cssText = `
      flex: 1;
      padding: 6px 8px;
      background: #ffffff;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      color: #334155;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.15s ease;
    `;
    this.zoomBtn.addEventListener('mouseenter', () => { this.zoomBtn.style.background = '#f1f5f9'; });
    this.zoomBtn.addEventListener('mouseleave', () => { this.zoomBtn.style.background = '#ffffff'; });
    this.zoomBtn.addEventListener('click', () => {
      this.callbacks.onZoomSelected?.();
    });
    this.updateZoomButtonLabel();
    toolbar.appendChild(this.zoomBtn);

    this.isolateBtn = document.createElement('button');
    this.isolateBtn.textContent = '👁️ Isolate';
    this.isolateBtn.title = 'Show only selected object, temporarily hide all others';
    this.isolateBtn.style.cssText = `
      flex: 1;
      padding: 6px 8px;
      background: #ffffff;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      color: #334155;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.15s ease;
    `;
    this.isolateBtn.addEventListener('click', () => {
      this.toggleIsolate();
    });
    toolbar.appendChild(this.isolateBtn);

    this.el.appendChild(toolbar);
  }

  private renderTreeNodes(): void {
    if (!this.contentContainer) return;
    this.contentContainer.innerHTML = '';

    const objects = this.schema.objects;
    if (!objects || objects.length === 0) {
      this.contentContainer.innerHTML = '<div style="padding:8px;color:#94a3b8;">No Object data available.</div>';
      return;
    }

    for (const obj of objects) {
      const objId = objectKey(obj);
      const objRow = document.createElement('div');
      objRow.dataset.objectId = objId;
      const isObjSelected = this.selectedTarget.type === 'object' && this.selectedTarget.id === objId;
      const isObjVisible = obj.parts.some(p => this.partVisibility.get(p.groupId) !== false);

      objRow.style.cssText = `
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 5px 6px;
        border-radius: 4px;
        background: ${isObjSelected ? 'rgba(37, 99, 235, 0.12)' : '#f1f5f9'};
        border: 1px solid ${isObjSelected ? '#2563eb' : '#e2e8f0'};
        cursor: pointer;
        transition: all 0.15s ease;
      `;

      const eyeBtn = document.createElement('span');
      eyeBtn.textContent = isObjVisible ? '👁️' : '🚫';
      eyeBtn.title = isObjVisible ? 'Hide entire Object' : 'Show entire Object';
      eyeBtn.style.cssText = 'cursor:pointer;font-size:12px;opacity:0.85;user-select:none;';
      eyeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleObjectVisibility(obj, !isObjVisible);
      });
      objRow.appendChild(eyeBtn);

      const objTitle = document.createElement('span');
      objTitle.textContent = obj.name || obj.label || objId;
      objTitle.style.cssText = `
        flex: 1;
        font-weight: 700;
        font-size: 10.5px;
        color: ${isObjVisible ? '#1e293b' : '#94a3b8'};
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      `;
      objRow.appendChild(objTitle);

      const badge = document.createElement('span');
      badge.textContent = `${obj.parts.length}P`;
      badge.style.cssText = 'font-size:9px;background:#e2e8f0;padding:1px 4px;border-radius:3px;color:#64748b;';
      objRow.appendChild(badge);

      objRow.addEventListener('click', (e) => {
        const isMulti = e.shiftKey || e.ctrlKey || e.metaKey;
        this.selectTarget('object', objId, undefined, isMulti);
      });

      this.contentContainer.appendChild(objRow);

      const partsContainer = document.createElement('div');
      partsContainer.style.cssText = 'display:flex;flex-direction:column;gap:3px;margin-left:14px;padding-left:6px;border-left:2px solid #e2e8f0;';

      for (const part of obj.parts) {
        const id = partKey(part);
        const title = partDisplayTitle(part);
        const colorKey = part.colorKey ?? id;
        const isPartSelected = this.selectedGroupIds.has(part.groupId) ||
                               (this.selectedTarget.type === 'part' && this.selectedTarget.groupId === part.groupId) ||
                               (this.selectedTarget.type === 'object' && this.selectedTarget.id === objId);
        const isPartVisible = this.partVisibility.get(part.groupId) !== false;
        let currentColor = this.partColors.get(colorKey) || part.defaultColor || '#64748b';

        const partRow = document.createElement('div');
        partRow.dataset.partId = id;
        partRow.dataset.groupId = String(part.groupId);
        partRow.style.cssText = `
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 6px;
          border-radius: 4px;
          background: ${isPartSelected ? 'rgba(37, 99, 235, 0.12)' : '#ffffff'};
          border: 1px solid ${isPartSelected ? '#2563eb' : '#f1f5f9'};
          cursor: pointer;
          transition: all 0.15s ease;
          min-height: 24px;
          box-sizing: border-box;
        `;

        const pEyeBtn = document.createElement('span');
        pEyeBtn.textContent = isPartVisible ? '👁️' : '🚫';
        pEyeBtn.title = isPartVisible ? 'Hide this Part' : 'Show this Part';
        pEyeBtn.style.cssText = 'cursor:pointer;font-size:11px;opacity:0.85;user-select:none;display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;flex-shrink:0;';
        pEyeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.togglePartVisibility(part.groupId, !isPartVisible);
        });
        partRow.appendChild(pEyeBtn);

        const swatchBtn = document.createElement('button');
        swatchBtn.type = 'button';
        swatchBtn.title = `Select color for ${title} (Bambu Lab Palette)`;
        swatchBtn.style.cssText = `
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 1.5px solid #cbd5e1;
          background-color: ${currentColor};
          cursor: pointer;
          padding: 0;
          margin: 0;
          flex-shrink: 0;
          display: inline-block;
          vertical-align: middle;
          box-sizing: border-box;
          transition: transform 0.1s ease, border-color 0.1s ease;
          box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1);
        `;
        swatchBtn.addEventListener('mouseenter', () => {
          swatchBtn.style.transform = 'scale(1.15)';
          swatchBtn.style.borderColor = '#64748b';
        });
        swatchBtn.addEventListener('mouseleave', () => {
          swatchBtn.style.transform = 'scale(1)';
          swatchBtn.style.borderColor = '#cbd5e1';
        });
        swatchBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.openBambuColorPopover(swatchBtn, colorKey, title, currentColor, (newHex) => {
            swatchBtn.style.backgroundColor = newHex;
            currentColor = newHex;
          });
        });
        partRow.appendChild(swatchBtn);

        const partTitle = document.createElement('span');
        partTitle.textContent = title;
        partTitle.style.cssText = `
          flex: 1;
          font-size: 10px;
          font-weight: 500;
          line-height: 1.2;
          display: inline-flex;
          align-items: center;
          color: ${isPartVisible ? '#334155' : '#94a3b8'};
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        `;
        partRow.appendChild(partTitle);

        partRow.addEventListener('click', (e) => {
          e.stopPropagation();
          const isMulti = e.shiftKey || e.ctrlKey || e.metaKey;
          this.selectTarget('part', id, part.groupId, isMulti);
        });

        partsContainer.appendChild(partRow);
      }

      this.contentContainer.appendChild(partsContainer);
    }
  }

  private toggleObjectVisibility(obj: ObjectSchema, visible: boolean): void {
    for (const part of obj.parts) {
      this.partVisibility.set(part.groupId, visible);
    }
    this.renderTreeNodes();
    this.callbacks.onVisibilityChange?.(this.partVisibility);
  }

  private togglePartVisibility(groupId: number, visible: boolean): void {
    this.partVisibility.set(groupId, visible);
    this.renderTreeNodes();
    this.callbacks.onVisibilityChange?.(this.partVisibility);
  }

  public selectTarget(type: 'all' | 'object' | 'part', id: string, groupId?: number, isMulti = false): void {
    if (type === 'all') {
      this.selectedGroupIds.clear();
      this.selectedTarget = { type: 'all', id: 'all' };
    } else if (type === 'object') {
      const obj = this.schema.objects?.find(o => o.id === id);
      const gids = obj ? obj.parts.map(p => p.groupId) : [];
      if (isMulti) {
        const allIn = gids.every(g => this.selectedGroupIds.has(g));
        gids.forEach(g => allIn ? this.selectedGroupIds.delete(g) : this.selectedGroupIds.add(g));
      } else {
        this.selectedGroupIds.clear();
        gids.forEach(g => this.selectedGroupIds.add(g));
      }
      this.selectedTarget = this.selectedGroupIds.size > 0 ? { type: 'object', id } : { type: 'all', id: 'all' };
    } else if (type === 'part' && groupId !== undefined) {
      if (isMulti) {
        if (this.selectedGroupIds.has(groupId)) {
          this.selectedGroupIds.delete(groupId);
        } else {
          this.selectedGroupIds.add(groupId);
        }
      } else {
        this.selectedGroupIds.clear();
        this.selectedGroupIds.add(groupId);
      }
      this.selectedTarget = this.selectedGroupIds.size > 0 ? { type: 'part', id, groupId } : { type: 'all', id: 'all' };
    }

    if (this.isIsolated) {
      const activeGroupIds = new Set<number>();
      if (this.selectedGroupIds.size > 0) {
        for (const gid of this.selectedGroupIds) {
          activeGroupIds.add(gid);
        }
      } else if (this.selectedTarget.type === 'object') {
        const obj = this.schema.objects?.find(o => o.id === this.selectedTarget.id);
        obj?.parts.forEach(p => activeGroupIds.add(p.groupId));
      } else if (this.selectedTarget.type === 'part' && this.selectedTarget.groupId !== undefined) {
        activeGroupIds.add(this.selectedTarget.groupId);
      }

      if (activeGroupIds.size > 0) {
        for (const [gid] of this.partVisibility) {
          this.partVisibility.set(gid, activeGroupIds.has(gid));
        }
      }
      this.callbacks.onVisibilityChange?.(this.partVisibility);
    }

    this.renderTreeNodes();
    this.updateZoomButtonLabel();
    this.callbacks.onSelect?.(type, id, groupId);
  }

  private updateZoomButtonLabel(): void {
    if (!this.zoomBtn) return;
    const hasSelection = this.selectedGroupIds.size > 0;
    this.zoomBtn.textContent = hasSelection ? '⛶ Zoom Selected' : '⛶ Zoom All';
    this.zoomBtn.title = hasSelection
      ? 'Center camera view on selected object or part'
      : 'Fit the camera to the entire model';
  }

  public getSelectedGroupIds(): Set<number> {
    return new Set(this.selectedGroupIds);
  }

  public toggleIsolate(): void {
    if (!this.isIsolated) {
      this.isIsolated = true;
      this.isolateBtn.style.background = '#2563eb';
      this.isolateBtn.style.color = '#ffffff';
      this.isolateBtn.style.borderColor = '#2563eb';
      this.isolateBtn.textContent = '✓ Isolating';

      this.preIsolateVisibility = new Map(this.partVisibility);

      const activeGroupIds = new Set<number>();
      if (this.selectedGroupIds.size > 0) {
        for (const gid of this.selectedGroupIds) {
          activeGroupIds.add(gid);
        }
      } else if (this.selectedTarget.type === 'object') {
        const obj = this.schema.objects?.find(o => o.id === this.selectedTarget.id);
        obj?.parts.forEach(p => activeGroupIds.add(p.groupId));
      } else if (this.selectedTarget.type === 'part' && this.selectedTarget.groupId !== undefined) {
        activeGroupIds.add(this.selectedTarget.groupId);
      }

      if (activeGroupIds.size > 0) {
        for (const [gid] of this.partVisibility) {
          this.partVisibility.set(gid, activeGroupIds.has(gid));
        }
      }

      this.renderTreeNodes();
      this.callbacks.onVisibilityChange?.(this.partVisibility);
      this.callbacks.onIsolateToggle?.(true);
    } else {
      this.isIsolated = false;
      this.isolateBtn.style.background = '#ffffff';
      this.isolateBtn.style.color = '#334155';
      this.isolateBtn.style.borderColor = '#cbd5e1';
      this.isolateBtn.textContent = '👁️ Isolate';

      this.partVisibility = new Map(this.preIsolateVisibility);
      this.renderTreeNodes();
      this.callbacks.onVisibilityChange?.(this.partVisibility);
      this.callbacks.onIsolateToggle?.(false);
    }
  }

  public getVisibilityMap(): Map<number, boolean> {
    return this.partVisibility;
  }

  public isPartVisible(groupId: number): boolean {
    return this.partVisibility.get(groupId) !== false;
  }

  public getIsIsolated(): boolean {
    return this.isIsolated;
  }

  public getSelectedTarget(): { type: 'all' | 'object' | 'part' | 'multi'; id: string; groupId?: number } {
    return this.selectedTarget;
  }

  public setPartColor(colorKey: string, hex: string): void {
    this.partColors.set(colorKey, hex);
    this.renderTreeNodes();
  }

  private openBambuColorPopover(
    anchorEl: HTMLElement,
    colorKey: string,
    partLabel: string,
    currentColor: string,
    onColorSelect: (newHex: string) => void
  ): void {
    const existing = document.getElementById('bambu-color-popover');
    if (existing) {
      const wasThisAnchor = (existing as any)._anchor === anchorEl;
      existing.remove();
      if (wasThisAnchor) return;
    }

    const popover = document.createElement('div');
    popover.id = 'bambu-color-popover';
    (popover as any)._anchor = anchorEl;
    popover.style.cssText = `
      position: fixed;
      z-index: 9999;
      background: #ffffff;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 10px;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
      width: 215px;
      font-family: var(--mono-font, ui-monospace, monospace);
      pointer-events: auto;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid #f1f5f9;
    `;
    header.innerHTML = `
      <div style="font-size: 11px; font-weight: 700; color: #1e293b;">Bambu Lab Palette</div>
      <div style="font-size: 9px; color: #64748b; font-weight: 500;">10 PLA Colors</div>
    `;
    popover.appendChild(header);

    const grid = document.createElement('div');
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 6px;
      margin-bottom: 8px;
    `;

    for (const color of BAMBU_LAB_COLORS) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.title = `${color.name} (${color.desc})\n${color.hex}`;
      const isSelected = currentColor.toUpperCase() === color.hex.toUpperCase();
      dot.style.cssText = `
        width: 34px;
        height: 34px;
        border-radius: 6px;
        background-color: ${color.hex};
        border: ${isSelected ? '2px solid #2563eb' : '1px solid #cbd5e1'};
        box-shadow: ${isSelected ? '0 0 0 2px rgba(37,99,235,0.25)' : 'none'};
        cursor: pointer;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.1s ease, border-color 0.1s ease;
      `;
      if (isSelected) {
        dot.innerHTML = `<span style="color: ${color.hex === '#FFFFFF' || color.hex === '#F7D117' || color.hex === '#F472B6' ? '#0f172a' : '#ffffff'}; font-size: 13px; font-weight: bold; line-height: 1;">✓</span>`;
      }

      dot.addEventListener('mouseenter', () => {
        dot.style.transform = 'scale(1.12)';
        dot.style.zIndex = '2';
      });
      dot.addEventListener('mouseleave', () => {
        dot.style.transform = 'scale(1)';
        dot.style.zIndex = '1';
      });
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        this.partColors.set(colorKey, color.hex);
        this.callbacks.onColorChange?.(colorKey, color.hex);
        onColorSelect(color.hex);
        popover.remove();
      });
      grid.appendChild(dot);
    }
    popover.appendChild(grid);

    const footer = document.createElement('div');
    footer.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 6px;
      border-top: 1px solid #f1f5f9;
      font-size: 10px;
      color: #64748b;
    `;
    footer.innerHTML = `<span>Custom Color:</span>`;

    const customInput = document.createElement('input');
    customInput.type = 'color';
    customInput.value = currentColor;
    customInput.title = 'Open full color picker';
    customInput.style.cssText = `
      width: 26px;
      height: 22px;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      cursor: pointer;
      padding: 0;
      background: none;
    `;
    customInput.addEventListener('input', (e) => {
      e.stopPropagation();
      const val = customInput.value;
      this.partColors.set(colorKey, val);
      this.callbacks.onColorChange?.(colorKey, val);
      onColorSelect(val);
    });
    footer.appendChild(customInput);
    popover.appendChild(footer);

    document.body.appendChild(popover);
    const rect = anchorEl.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let top = rect.bottom + 6;
    let left = rect.left;

    if (top + popoverRect.height > window.innerHeight - 10) {
      top = rect.top - popoverRect.height - 6;
    }
    if (left + popoverRect.width > window.innerWidth - 10) {
      left = window.innerWidth - popoverRect.width - 10;
    }

    popover.style.top = `${Math.max(10, top)}px`;
    popover.style.left = `${Math.max(10, left)}px`;

    const onDocClick = (e: MouseEvent) => {
      if (!popover.contains(e.target as Node) && e.target !== anchorEl) {
        popover.remove();
        document.removeEventListener('click', onDocClick);
      }
    };
    setTimeout(() => {
      document.addEventListener('click', onDocClick);
    }, 10);
  }
}
